import React, { useMemo } from "react";
import { Button } from "../components/ui/button";
import { Download, Trash2, Plus, X, Link as LinkIcon, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

export function TransactionFilters({
  fType, setFType,
  fAccount, setFAccount, accounts,
  fTag, setFTag, tags,
  fPaymentMethod, setFPaymentMethod, paymentMethods,
  fStartDate, setFStartDate,
  fEndDate, setFEndDate,
  fSearch, setFSearch,
  handleExport, isAdmin
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="w-full pb-2 border-b border-border/50">
        <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground mb-1 block">Genel Arama</Label>
        <Input 
          type="text" 
          className="h-9 w-full md:max-w-md text-xs" 
          placeholder="Açıklama, hesap adı veya tutar ara..." 
          value={fSearch} 
          onChange={e => setFSearch(e.target.value)} 
        />
      </div>
      
      <div className="flex flex-col md:flex-row items-end justify-between gap-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 w-full md:w-auto flex-1">
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Tür</Label>
            <Select value={fType} onValueChange={setFType}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="income">Gelir</SelectItem>
                <SelectItem value="expense">Gider</SelectItem>
                <SelectItem value="debt_payment">Borç Ödeme</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Hesap</Label>
              <Select value={fAccount} onValueChange={setFAccount}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Ödeme Yön.</Label>
            <Select value={fPaymentMethod} onValueChange={setFPaymentMethod}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {paymentMethods?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Etiket</Label>
            <Select value={fTag} onValueChange={setFTag}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {tags.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Başlangıç</Label>
            <Input type="date" className="h-9 text-xs" value={fStartDate} onChange={e => setFStartDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Bitiş</Label>
            <Input type="date" className="h-9 text-xs" value={fEndDate} onChange={e => setFEndDate(e.target.value)} />
          </div>
        </div>
        <Button variant="outline" onClick={handleExport} className="shrink-0 h-9 rounded-sm" data-testid="export-excel-button">
          <Download size={14} className="mr-2" /> Excel İndir
        </Button>
      </div>
    </div>
  );
}

export function TransactionTable({ transactions, tags, handleDelete, handleEditClick, handleMatchInvoice, isAdmin }) {
  const [sortConfig, setSortConfig] = React.useState({ key: 'date', direction: 'desc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <span className="ml-1 opacity-20 group-hover:opacity-50 inline-block w-3">↕</span>;
    return <span className="ml-1 inline-block w-3 text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  const displayTxs = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => {
      const timeDiff = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (timeDiff !== 0) return timeDiff;
      return (a.id || "").localeCompare(b.id || "");
    });

    const balances = {};
    const withBalances = sorted.map(tx => {
      if (!balances[tx.currency]) balances[tx.currency] = 0;
      if (tx.type === 'income') balances[tx.currency] += tx.amount;
      else balances[tx.currency] -= tx.amount; // expense and debt_payment
      
      return { ...tx, runningBalance: balances[tx.currency] };
    });

    let sortedWithBalances = [...withBalances];
    if (sortConfig.key) {
      sortedWithBalances.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'amount' || sortConfig.key === 'runningBalance') {
            aVal = Number(aVal);
            bVal = Number(bVal);
        } else if (sortConfig.key === 'date') {
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
        } else {
            aVal = String(aVal || "").toLowerCase();
            bVal = String(bVal || "").toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        
        const idA = a.id || "";
        const idB = b.id || "";
        if (idA < idB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (idA > idB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      sortedWithBalances.reverse();
    }

    return sortedWithBalances;
  }, [transactions, sortConfig]);

  return (
    <div className="bg-white border border-border rounded-sm overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.02)] mt-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left whitespace-nowrap">
          <thead className="bg-background text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground border-b border-border select-none">
            <tr>
              <th className="px-5 py-4 cursor-pointer hover:bg-neutral-100 group transition-colors" onClick={() => handleSort('date')}>
                <div className="flex items-center">Tarih <SortIcon columnKey="date" /></div>
              </th>
              <th className="px-5 py-4 cursor-pointer hover:bg-neutral-100 group transition-colors" onClick={() => handleSort('account_name')}>
                <div className="flex items-center">Hesap / Kişi <SortIcon columnKey="account_name" /></div>
              </th>
              <th className="px-5 py-4 cursor-pointer hover:bg-neutral-100 group transition-colors" onClick={() => handleSort('description')}>
                <div className="flex items-center">Açıklama <SortIcon columnKey="description" /></div>
              </th>
              <th className="px-5 py-4">Etiketler</th>
              <th className="px-5 py-4 cursor-pointer hover:bg-neutral-100 group transition-colors" onClick={() => handleSort('payment_method_name')}>
                <div className="flex items-center">Ödeme Yön. <SortIcon columnKey="payment_method_name" /></div>
              </th>
              <th className="px-5 py-4 cursor-pointer hover:bg-neutral-100 group transition-colors" onClick={() => handleSort('type')}>
                <div className="flex items-center">Tür <SortIcon columnKey="type" /></div>
              </th>
              <th className="px-5 py-4 text-right cursor-pointer hover:bg-neutral-100 group transition-colors" onClick={() => handleSort('amount')}>
                <div className="flex items-center justify-end">Tutar <SortIcon columnKey="amount" /></div>
              </th>
              <th className="px-5 py-4 text-center">Fatura</th>
              <th className="px-5 py-4 text-right cursor-pointer hover:bg-neutral-100 group transition-colors" onClick={() => handleSort('runningBalance')}>
                <div className="flex items-center justify-end">Kasa Neti <SortIcon columnKey="runningBalance" /></div>
              </th>
              {isAdmin && <th className="px-5 py-4 text-center"></th>}
            </tr>
          </thead>
          <tbody>
            {displayTxs.length === 0 ? (
              <tr><td colSpan={isAdmin ? "10" : "9"} className="text-center py-10 text-muted-foreground">İşlem bulunamadı.</td></tr>
            ) : (
              displayTxs.map(tx => (
                <tr key={tx.id} className="border-b border-border last:border-0 hover:bg-neutral-50/50 transition-colors">
                  <td className="px-5 py-4">{new Date(tx.date).toLocaleDateString('tr-TR')}</td>
                  <td className="px-5 py-4 font-medium">
                    {tx.account_id ? (
                      <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-primary/30"></span>{tx.account_name}</span>
                    ) : (
                      <span className="text-muted-foreground">{tx.account_name || "-"}</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-muted-foreground max-w-[200px] group relative">
                    <div className="truncate group-hover:whitespace-normal group-hover:break-words">{tx.description}</div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-1.5 flex-wrap">
                      {tx.tags.map(tid => {
                        const tag = tags.find(t => t.id === tid);
                        return tag ? (
                          <span key={tid} className="px-2 py-0.5 rounded-sm text-[10px] font-medium text-white shadow-sm" style={{ backgroundColor: tag.color }}>
                            {tag.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-muted-foreground">
                    {tx.payment_method_name || "-"}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 rounded-[2px] text-[10px] font-bold uppercase tracking-wider ${tx.type === 'income' ? 'bg-income-bg text-income' : tx.type === 'debt_payment' ? 'bg-blue-100 text-blue-700' : 'bg-expense-bg text-expense'}`}>
                      {tx.type === 'income' ? 'Gelir' : tx.type === 'debt_payment' ? 'Borç Ödeme' : 'Gider'}
                    </span>
                  </td>
                  <td className={`px-5 py-4 text-right font-medium font-heading tracking-tight text-base ${tx.type === 'income' ? 'text-income' : tx.type === 'debt_payment' ? 'text-blue-700' : 'text-expense'}`}>
                    {tx.type === 'income' ? '+' : '-'}{tx.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {tx.currency}
                  </td>
                  <td className="px-5 py-4 text-center">
                    {tx.invoice_id ? (
                      <span className="inline-flex items-center text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded cursor-pointer hover:bg-green-100" onClick={() => handleMatchInvoice(tx)} title="Faturaya Gözat">
                        <LinkIcon className="w-3 h-3 mr-1" /> Eşleşti
                      </span>
                    ) : (
                      <Button variant="outline" size="sm" className="h-7 text-[10px] px-2" onClick={() => handleMatchInvoice(tx)}>
                        <CreditCard className="w-3 h-3 mr-1" /> Fatura Seç
                      </Button>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right font-medium font-heading tracking-tight text-base text-foreground">
                    {tx.runningBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {tx.currency}
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10" onClick={() => handleEditClick(tx)} data-testid={`edit-tx-${tx.id}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-edit"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2-2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10" onClick={() => {
                            toast("Emin misiniz?", {
                              action: {
                                label: "Evet, Sil",
                                onClick: () => handleDelete(tx.id)
                              }
                            });
                        }} data-testid={`delete-tx-${tx.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TransactionForm({
  handleCreate, type, setType, date, setDate, amount, setAmount, currency, setCurrency,
  accountId, setAccountId, accountName, setAccountName,
  paymentMethodId, setPaymentMethodId, paymentMethods,
  description, setDescription, accounts, accDialogOpen, setAccDialogOpen,
  manualAccName, setManualAccName, selectedTags, tags, toggleTag,
  tagDialogOpen, setTagDialogOpen, newTag, setNewTag, handleAddNewTag, handleUpload
}) {
  return (
    <form onSubmit={handleCreate} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
        <div className="space-y-2">
          <Label>İşlem Türü</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-10 bg-white" data-testid="tx-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="income">Gelir (Para Girişi)</SelectItem>
              <SelectItem value="expense">Gider (Para Çıkışı)</SelectItem>
              <SelectItem value="debt_payment">Borç Ödeme</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Tarih</Label>
          <Input type="date" className="h-10 bg-white" value={date} onChange={e => setDate(e.target.value)} required data-testid="tx-date-input"/>
        </div>
        <div className="space-y-2">
          <Label>Miktar</Label>
          <Input type="number" step="0.01" className="h-10 font-heading text-base bg-white" value={amount} onChange={e => setAmount(e.target.value)} required placeholder="0.00" data-testid="tx-amount-input"/>
        </div>
        <div className="space-y-2">
          <Label>Para Birimi</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="h-10 bg-white" data-testid="tx-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TL">TL</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2 lg:col-span-2 flex flex-col justify-end">
          <Label>Cari Hesap / Kişi</Label>
          <Button
            type="button"
            variant="outline"
            className={`w-full justify-start text-left font-normal h-10 bg-white ${accountId === "manual" && !accountName ? "text-muted-foreground" : "text-foreground"}`}
            onClick={() => setAccDialogOpen(true)}
            data-testid="open-account-modal"
          >
            {accountId === "manual" && accountName 
              ? <span className="flex items-center"><span className="px-1.5 py-0.5 rounded-[2px] bg-neutral-200 text-neutral-700 text-[10px] font-bold uppercase tracking-wider mr-2">Elle Girildi</span> {accountName}</span>
              : accountId !== "manual"
                ? <span className="flex items-center"><span className="px-1.5 py-0.5 rounded-[2px] bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider mr-2">Kayıtlı Hesap</span> {accounts.find(a => a.id === accountId)?.name || "Seçim yapılmadı"}</span>
                : "Hesap veya Kişi seçin..."
            }
          </Button>
          
          <Dialog open={accDialogOpen} onOpenChange={setAccDialogOpen}>
            <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden gap-0 rounded-sm">
              <DialogHeader className="p-5 border-b border-border bg-white">
                <DialogTitle className="font-heading tracking-tight">Cari Hesap Seç</DialogTitle>
              </DialogHeader>
              
              <div className="max-h-[40vh] overflow-y-auto p-2 bg-background space-y-1">
                {accounts.map(a => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      setAccountId(a.id);
                      setAccountName(a.name);
                      setAccDialogOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-sm text-sm transition-all border ${
                      accountId === a.id 
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm' 
                        : 'bg-white border-transparent hover:border-border hover:shadow-sm text-foreground'
                    }`}
                  >
                    <span className="font-medium">{a.name}</span>
                  </button>
                ))}
                {accounts.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-6">Kayıtlı hesap yok.</div>
                )}
              </div>
              
              <div className="p-5 border-t border-border bg-white">
                <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3 block">Listede Yoksa Elle Girin</Label>
                <div className="flex gap-2">
                  <Input 
                    className="h-10 rounded-sm focus-visible:ring-1"
                    placeholder="Firma veya Kişi adı..." 
                    value={manualAccName}
                    onChange={(e) => setManualAccName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if(manualAccName.trim()) {
                          setAccountId("manual");
                          setAccountName(manualAccName.trim());
                          setAccDialogOpen(false);
                        }
                      }
                    }}
                    data-testid="manual-account-input"
                  />
                  <Button type="button" className="h-10 rounded-sm px-5" onClick={() => {
                    if(manualAccName.trim()) {
                      setAccountId("manual");
                      setAccountName(manualAccName.trim());
                      setAccDialogOpen(false);
                    }
                  }} data-testid="manual-account-btn">Seç</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-2 lg:col-span-2">
          <Label>Ödeme Yöntemi</Label>
          <Select value={paymentMethodId} onValueChange={setPaymentMethodId}>
            <SelectTrigger className="h-10 bg-white" data-testid="tx-payment-method-select"><SelectValue placeholder="Seçim yapılmadı" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Seçim Yapılmadı --</SelectItem>
              {paymentMethods?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 lg:col-span-4">
          <Label>Açıklama</Label>
          <Input className="h-10 bg-white" value={description} onChange={e => setDescription(e.target.value)} placeholder="Açıklama (opsiyonel)" data-testid="tx-description-input" />
        </div>
      </div>
      
      <div className="space-y-2 pt-2">
        <Label>Etiketler</Label>
        <div className="flex flex-wrap gap-2 items-center min-h-[44px] p-1.5 border border-input rounded-sm bg-white">
          {selectedTags.map(tid => {
            const tag = tags.find(t => t.id === tid);
            return tag ? (
              <span key={tid} className="px-2.5 py-1.5 rounded-sm text-xs font-medium text-white flex items-center gap-1.5 shrink-0" style={{ backgroundColor: tag.color }}>
                {tag.name}
                <button type="button" onClick={() => toggleTag(tid)} className="hover:text-black/50 ml-0.5 focus:outline-none">
                  <X size={12} />
                </button>
              </span>
            ) : null;
          })}
          
          <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="h-8 text-xs border-dashed shrink-0 ml-1 hover:bg-neutral-100" data-testid="open-tags-modal">
                <Plus size={14} className="mr-1" /> Etiket Seç / Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden gap-0 rounded-sm">
              <DialogHeader className="p-5 border-b border-border bg-white">
                <DialogTitle className="font-heading tracking-tight">Etiket Seç</DialogTitle>
              </DialogHeader>
              
              <div className="max-h-[40vh] overflow-y-auto p-5 bg-background">
                <div className="grid grid-cols-2 gap-3">
                  {tags.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTag(t.id)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-sm text-left border transition-all ${
                        selectedTags.includes(t.id) 
                          ? 'bg-primary text-primary-foreground border-primary shadow-sm' 
                          : 'bg-white border-border hover:border-primary/30 hover:bg-white hover:shadow-sm text-foreground'
                      }`}
                    >
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color }}></div>
                      <span className="truncate font-medium">{t.name}</span>
                    </button>
                  ))}
                  {tags.length === 0 && (
                    <div className="col-span-2 text-sm text-muted-foreground text-center py-4">Kayıtlı etiket bulunamadı.</div>
                  )}
                </div>
              </div>
              
              <div className="p-5 border-t border-border bg-white">
                <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3 block">Listede Yoksa Yeni Oluştur</Label>
                <div className="flex gap-2">
                  <Input 
                    className="h-10 rounded-sm focus-visible:ring-1"
                    placeholder="Örn: Yemek, Ulaşım..." 
                    value={newTag} 
                    onChange={(e) => setNewTag(e.target.value)} 
                    onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); handleAddNewTag(); } }}
                    data-testid="new-tag-input"
                  />
                  <Button type="button" className="h-10 rounded-sm px-5" onClick={handleAddNewTag} data-testid="add-new-tag-btn">Ekle</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="pt-6 border-t border-border mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label htmlFor="excel-upload" className="cursor-pointer">
            <div className="flex items-center gap-2 h-11 px-4 rounded-sm text-sm uppercase tracking-wider font-semibold border border-input bg-white hover:bg-neutral-100 transition-colors">
              <Download size={16} className="rotate-180" /> Excel Yükle
            </div>
          </Label>
          <Input 
            id="excel-upload" 
            type="file" 
            accept=".xlsx, .xls" 
            className="hidden" 
            onChange={handleUpload} 
            data-testid="excel-upload-input"
          />
        </div>
        <Button type="submit" className="h-11 px-8 rounded-sm text-sm uppercase tracking-wider font-semibold" data-testid="create-tx-button">
          <Plus size={16} className="mr-2" /> İşlemi Kaydet
        </Button>
      </div>
    </form>
  );
}
